import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr, fmtQty, todayISO } from "../../api";

export default function PackingListCreate() {
  const [lots, setLots] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState({});
  const [customer, setCustomer] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/lots").then((r) => setLots(r.data.filter((l) => l.current_qty > 0 && !(l.box_count > 0)))).catch(() => {});
    api.get("/boxes", { params: { in_stock: true } }).then((r) => setBoxes(r.data)).catch(() => {});
    Promise.all([api.get("/packing-lists"), api.get("/deductions")]).then(([pl, dd]) => {
      const names = new Set();
      pl.data.forEach((p) => p.customer && names.add(p.customer));
      dd.data.forEach((d) => d.customer_name && names.add(d.customer_name));
      setCustomers([...names].sort());
    }).catch(() => {});
  }, []);

  const options = [
    ...boxes.map((b) => ({ key: `box:${b.id}`, kind: "box", id: b.id, lot_number: b.lot_number, box_number: b.box_number, product_name: b.product_name, available: b.remaining_qty, uom: b.uom, qc_status: b.qc?.status })),
    ...lots.map((l) => ({ key: `lot:${l.id}`, kind: "lot", id: l.id, lot_number: l.lot_number, box_number: null, product_name: l.product_name, available: l.current_qty, uom: l.uom, qc_status: l.qc?.status })),
  ];

  const visible = options.filter((o) =>
    !search || o.lot_number.toLowerCase().includes(search.toLowerCase()) || o.product_name.toLowerCase().includes(search.toLowerCase()));

  const toggle = (opt) => {
    setSelected((s) => {
      const n = { ...s };
      if (n[opt.key]) delete n[opt.key];
      else n[opt.key] = { opt, qty: opt.available };
      return n;
    });
  };

  const setQty = (key, qty) => setSelected((s) => ({ ...s, [key]: { ...s[key], qty } }));

  const chosen = Object.values(selected);
  const total = chosen.reduce((a, c) => a + (parseFloat(c.qty) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const items = chosen.map((c) => ({
        qty: parseFloat(c.qty) || c.opt.available,
        ...(c.opt.kind === "box" ? { box_id: c.opt.id } : { lot_id: c.opt.id }),
      }));
      const r = await api.post("/packing-lists", { customer, invoice_number: invoiceNumber, date, notes, items });
      toast.success(`${r.data.pl_number} created`);
      navigate(`/packing-list/${r.data.id}`);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="pl-create-page" className="space-y-6 max-w-4xl">
      <h1 className="font-heading text-3xl font-extrabold text-[#002B5D] tracking-tight">Create Packing List</h1>
      <form onSubmit={submit} className="space-y-6">
        <div className="sk-card p-6 grid sm:grid-cols-4 gap-4">
          <div>
            <label className="sk-label">Customer</label>
            <input data-testid="pl-customer-input" className="sk-input" list="pl-customer-list" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Customer name" />
            <datalist id="pl-customer-list">
              {customers.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <label className="sk-label">Invoice Number</label>
            <input data-testid="pl-invoice-input" className="sk-input font-mono" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="e.g. INV-1001" />
          </div>
          <div>
            <label className="sk-label">Date</label>
            <input data-testid="pl-date-input" type="date" className="sk-input" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label className="sk-label">Notes</label>
            <input data-testid="pl-notes-input" className="sk-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="sk-card">
          <div className="px-5 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-heading font-bold text-[#002B5D]">Select Lots / Boxes</h3>
            <input data-testid="pl-lot-search" placeholder="Filter lots..." value={search} onChange={(e) => setSearch(e.target.value)} className="sk-input max-w-[220px] h-9" />
          </div>
          <div className="max-h-96 overflow-auto">
            {visible.length === 0 && <div className="px-5 py-8 text-center text-slate-400 text-sm">No lots/boxes with available stock</div>}
            {visible.map((o) => {
              const sel = selected[o.key];
              return (
                <div key={o.key} data-testid={`pl-option-${o.lot_number}${o.box_number ? `-${o.box_number}` : ""}`}
                  className={`flex flex-wrap items-center gap-3 px-5 py-3 border-b border-border cursor-pointer transition-colors ${sel ? "bg-red-50/50" : "hover:bg-slate-50"}`}
                  onClick={() => toggle(o)}>
                  <input type="checkbox" readOnly checked={Boolean(sel)} className="w-4 h-4 pointer-events-none" data-testid={`pl-check-${o.key.replace(":", "-")}`} />
                  <div className="flex-1 min-w-[180px]">
                    <span className="font-mono font-semibold text-[#002B5D]">{o.lot_number}</span>
                    {o.box_number && <span className="font-mono font-semibold text-[#EB1C27] ml-2">{o.box_number}</span>}
                    <span className="text-slate-600 ml-2 text-sm">{o.product_name}</span>
                    <span className="text-slate-400 ml-2 text-xs">{o.qc_status}</span>
                  </div>
                  <div className="text-xs font-mono text-slate-500">avail {fmtQty(o.available)} {o.uom}</div>
                  {sel && (
                    <input data-testid={`pl-qty-${o.key.replace(":", "-")}`} type="number" step="any" min="0.001" max={o.available}
                      className="sk-input h-9 w-32 font-mono" value={sel.qty}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setQty(o.key, e.target.value)} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600" data-testid="pl-selected-summary">
            <span className="font-mono font-semibold">{chosen.length}</span> lot/box(es) selected · total <span className="font-mono font-semibold">{fmtQty(total)}</span>
          </div>
          <button data-testid="pl-create-submit" disabled={busy || chosen.length === 0}
            className="h-12 px-8 rounded-md font-heading font-bold text-white disabled:opacity-50" style={{ backgroundColor: "#EB1C27" }}>
            {busy ? "Creating..." : "Create Packing List"}
          </button>
        </div>
      </form>
    </div>
  );
}
